"use client";

import { useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Checkbox, Input, Label, Textarea } from "@eg/ui";
import { useTranslations } from "@/lib/i18n/I18nProvider";

type ContactFormValues = {
  name: string;
  email: string;
  message: string;
  consent: boolean;
};

export function ContactForm() {
  const t = useTranslations();
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  const contactSchema = useMemo(
    () =>
      z.object({
        name: z.string().min(2, t("contact.form.validation.name")),
        email: z.string().email(t("contact.form.validation.email")),
        message: z.string().min(10, t("contact.form.validation.message")),
        consent: z.boolean().refine((v) => v === true, {
          message: t("contact.form.validation.consent"),
        }),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: { consent: false, email: "", message: "", name: "" },
  });

  async function onSubmit(values: ContactFormValues) {
    setStatus("idle");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      if (!res.ok) throw new Error("Request failed");
      reset();
      setStatus("success");
    } catch (error) {
      console.error(error);
      setStatus("error");
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="contact-name">{t("contact.form.name")}</Label>
        <Input id="contact-name" autoComplete="name" {...register("name")} />
        {errors.name && (
          <p className="text-sm text-error">{errors.name.message}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact-email">{t("contact.form.email")}</Label>
        <Input
          id="contact-email"
          type="email"
          autoComplete="email"
          {...register("email")}
        />
        {errors.email && (
          <p className="text-sm text-error">{errors.email.message}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact-message">{t("contact.form.message")}</Label>
        <Textarea id="contact-message" rows={6} {...register("message")} />
        {errors.message && (
          <p className="text-sm text-error">{errors.message.message}</p>
        )}
      </div>
      <Controller
        control={control}
        name="consent"
        render={({ field }) => (
          <div className="flex items-start gap-3 rounded-md border border-gray-200 p-3">
            <Checkbox
              id="contact-consent"
              checked={field.value}
              onCheckedChange={(checked) => field.onChange(checked === true)}
            />
            <div className="space-y-1">
              <Label htmlFor="contact-consent" className="font-medium">
                {t("contact.form.consentTitle")}
              </Label>
              <p className="text-sm text-gray-600">
                {t("contact.form.consentCopy")}
              </p>
              {errors.consent && (
                <p className="text-sm text-error">{errors.consent.message}</p>
              )}
            </div>
          </div>
        )}
      />
      <div className="flex items-center justify-between gap-3">
        {status === "success" && (
          <p className="text-sm text-success">{t("contact.form.success")}</p>
        )}
        {status === "error" && (
          <p className="text-sm text-error">{t("contact.form.error")}</p>
        )}
        <Button type="submit" disabled={isSubmitting} className="ml-auto">
          {isSubmitting
            ? t("contact.form.submitting")
            : t("contact.form.submit")}
        </Button>
      </div>
    </form>
  );
}
